import React, { useState, useRef } from 'react';
import { Button } from 'components/Button';
import { Modal } from 'components/Modal';
import { NavSelect } from './NavSelect';
import styles from 'css_modules/Modal.module.css';
import { ReactComponent as Bin } from 'assets/svg/2_bin.svg';
import { ReactComponent as Plus } from 'assets/svg/2_plus.svg';

export const TableSettingsModal = ({
    close,
    fields,
    activeFields,
    setActiveFields,
    save
}) => {
    const [newField, setNewField] = useState('');
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragging, setDragging] = useState(false);

    const containerRef = useRef(null);
    const positions = useRef(new Map());

    // ==========================
    // Build Options
    // ==========================
    const options = [];
    Object.keys(fields).forEach((key) => {
        Object.keys(fields[key].items).forEach((itemKey) => {
            options.push({
                id: `${key}.${itemKey}`,
                left: fields[key].items[itemKey].name,
                right: fields[key].name
            });
        });
    });

    const selectChange = (index, e) => {
        const value = e.target.value;
        const next = [...activeFields];
        next[index] = value;
        setActiveFields(next);
    };

    const removeSelect = (index) => {
        const next = [...activeFields];
        next.splice(index, 1);
        if (next.length) setActiveFields(next);
    };

    const availableOptions = (index) => {
        const used = activeFields.filter((_, i) => i !== index);
        return options.filter((o) => !used.includes(o.id));
    };

    const availableForNew = options.filter(
        (o) => !activeFields.includes(o.id)
    );

    const addSelectedField = () => {
        if (!newField) return;
        if (!activeFields.includes(newField)) {
            setActiveFields([...activeFields, newField]);
            setNewField('');
        }
    };

    // ==========================
    // Drag Logic (Direction Aware + FLIP)
    // ==========================

    const handleMouseDown = (index, locked) => {
        if (locked) return;
        setDraggedIndex(index);
        setDragging(true);
    };

    const handleMouseMove = (e) => {
        if (!dragging || draggedIndex === null) return;

        const container = containerRef.current;
        const rows = Array.from(container.querySelectorAll('[data-row]'));

        const mouseY = e.clientY;
        let newIndex = draggedIndex;

        rows.forEach((row, index) => {
            if (index === draggedIndex) return;

            const rect = row.getBoundingClientRect();
            const middleY = rect.top + rect.height / 2;

            if (index > draggedIndex && mouseY > middleY) {
                newIndex = index;
            }

            if (index < draggedIndex && mouseY < middleY) {
                newIndex = index;
            }
        });

        if (newIndex === draggedIndex) return;

        // Store old positions
        rows.forEach((row) => {
            positions.current.set(
                row.dataset.key,
                row.getBoundingClientRect()
            );
        });

        const reordered = [...activeFields];
        const [movedItem] = reordered.splice(draggedIndex, 1);
        reordered.splice(newIndex, 0, movedItem);

        setActiveFields(reordered);
        setDraggedIndex(newIndex);

        requestAnimationFrame(() => {
            const newRows = Array.from(
                container.querySelectorAll('[data-row]')
            );

            newRows.forEach((row) => {
                const oldRect = positions.current.get(row.dataset.key);
                const newRect = row.getBoundingClientRect();
                if (!oldRect) return;

                const deltaY = oldRect.top - newRect.top;

                if (deltaY) {
                    row.style.transform = `translateY(${deltaY}px)`;
                    row.style.transition = 'none';

                    requestAnimationFrame(() => {
                        row.style.transition =
                            'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)';
                        row.style.transform = '';
                    });
                }
            });
        });
    };

    const handleMouseUp = () => {
        setDragging(false);
        setDraggedIndex(null);
    };

    return (
        <div className={styles.ontop}>
            <Modal name="Nastavení sloupců" close={close}>
                <div
                    ref={containerRef}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    {/* Add Column */}
                    <div
                        className={styles.columnDiv}
                        style={{
                            width: '21rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '16px'
                        }}
                    >
                        <div style={{ paddingLeft: '1.6rem' }}>
                            <NavSelect
                                value={newField}
                                data={availableForNew}
                                onChange={(e) => setNewField(e.target.value)}
                                tableSettingsTop
                                tableSettings
                            />
                        </div>

                        <Plus
                            style={{
                                marginLeft: 'auto',
                                width: 18,
                                height: 18,
                                cursor: newField
                                    ? 'pointer'
                                    : 'not-allowed',
                                opacity: newField ? 0.8 : 0.3
                            }}
                            onClick={addSelectedField}
                        />
                    </div>

                    {/* Active Fields */}
                    {activeFields.map((value, index) => {
                        const locked =
                            value ===
                            'template.document_info.template_name';

                        const isDragged = draggedIndex === index;

                        return (
                            <div
                                key={value}
                                data-row
                                data-key={value}
                                className={styles.columnDiv}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '6px 8px',
                                    borderRadius: '6px',
                                    transition:
                                        'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
                                    willChange: isDragged
                                        ? 'transform'
                                        : undefined,
                                    transform: isDragged
                                        ? 'scale(1.02)'
                                        : undefined,
                                    zIndex: isDragged ? 100 : undefined,
                                    boxShadow: isDragged
                                        ? '0 8px 20px rgba(0,0,0,0.12)'
                                        : 'none',
                                    background: isDragged
                                        ? 'var(--light)'
                                        : 'transparent',
                                    cursor: dragging
                                        ? 'grabbing'
                                        : 'default'
                                }}
                            >
                                {/* Drag Handle */}
                                <div
                                    onMouseDown={() =>
                                        handleMouseDown(index, locked)
                                    }
                                    style={{
                                        cursor: locked
                                            ? 'not-allowed'
                                            : 'grab',
                                        userSelect: 'none',
                                        padding: '4px',
                                        color: 'var(--purple)'
                                    }}
                                >
                                    ☰
                                </div>

                                <NavSelect
                                    value={value}
                                    data={availableOptions(index)}
                                    onChange={(e) =>
                                        selectChange(index, e)
                                    }
                                    notNull
                                    tableSettings
                                />

                                <Bin
                                    style={{
                                        marginLeft: 'auto',
                                        width: 18,
                                        height: 18,
                                        cursor: locked
                                            ? 'not-allowed'
                                            : 'pointer',
                                        opacity: locked
                                            ? 0.3
                                            : 0.8
                                    }}
                                    onClick={() =>
                                        !locked && removeSelect(index)
                                    }
                                />
                            </div>
                        );
                    })}

                    {/* Save */}
                    <div
                        className={styles.columnDiv}
                        style={{ marginTop: '16px' }}
                    >
                        <Button primary onClick={save}>
                            Uložit
                        </Button>
                        <Button secondary onClick={close}>
                            Zrušit
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
